import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Calendar, Clock, User, Share2, 
  Facebook, Twitter, Linkedin, Copy, CheckCircle,
  ArrowRight, Tag
} from 'lucide-react';
import { toast } from 'sonner';

// Full blog post content
const blogPostsData = {
  'preparing-horse-for-boarding': {
    id: 1,
    title: 'How to Prepare Your Horse for Boarding: A Complete Guide',
    excerpt: 'Moving your horse to a new boarding facility can be stressful for both you and your equine companion. Here\'s everything you need to know to make the transition smooth.',
    image: 'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=1200&h=600&fit=crop',
    category: 'Horse Care',
    author: 'Sarah Mitchell',
    authorImage: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
    authorBio: 'Lifelong equestrian with 20+ years of experience in horse care and stable management.',
    date: '2026-01-02',
    readTime: '8 min read',
    content: `
## Introduction

Moving your horse to a new boarding facility can be one of the most stressful experiences for both you and your equine companion. Whether you're relocating, upgrading facilities, or finding a new home for your horse due to changing circumstances, proper preparation can make all the difference.

In this comprehensive guide, we'll walk you through everything you need to know to ensure a smooth transition for your horse.

## Before the Move

### 1. Research Your New Facility

Before committing to a new boarding facility, take time to thoroughly research your options:

- **Visit in person** - Photos can be deceiving. Always visit the facility to assess cleanliness, safety, and overall atmosphere.
- **Talk to current boarders** - They can provide honest feedback about their experiences.
- **Review the contract** - Understand all terms, fees, and policies before signing.
- **Check references** - Ask for and contact references from the facility.

### 2. Gather Medical Records

Ensure you have copies of:

- Vaccination records (most facilities require current vaccinations)
- Coggins test results (typically required annually)
- Any medical history or ongoing treatments
- Farrier schedule and records
- Dental records

### 3. Prepare Your Horse's Documents

Create a comprehensive file including:

- Registration papers
- Insurance information
- Emergency contact information
- Feeding instructions and preferences
- Any behavioral notes the new facility should know

## The Week Before

### Update Vaccinations if Needed

Most boarding facilities require:

- Rabies
- Eastern/Western Encephalomyelitis
- Tetanus
- West Nile Virus
- Influenza
- Rhinopneumonitis

Check with your new facility about their specific requirements and ensure your horse is current on all necessary vaccinations at least two weeks before the move.

### Pack Essentials

Gather everything your horse will need:

- Halter and lead rope (have a backup)
- Blankets appropriate for the season
- Grooming supplies
- Favorite treats
- Any supplements or medications
- Feeding buckets or containers

## Moving Day Tips

### Keep Calm

Horses are incredibly sensitive to our emotions. If you're stressed, your horse will pick up on it. Take deep breaths and maintain a calm demeanor throughout the process.

### Travel Safely

- Ensure your trailer is in good condition
- Provide adequate ventilation
- Offer water during longer trips
- Take breaks every 3-4 hours for trips over 4 hours
- Avoid traveling during extreme heat

### Arrival at the New Facility

When you arrive:

1. Let your horse look around from the trailer before unloading
2. Walk them around the property to familiarize them with the environment
3. Introduce them to their new stall or pasture gradually
4. Stay for a while to help them settle in

## The First Few Weeks

### Monitor Closely

Watch for signs of stress:

- Changes in eating or drinking habits
- Excessive sweating
- Pacing or weaving
- Changes in manure consistency

### Maintain Routine

Try to keep feeding times and routines as consistent as possible with what your horse was used to.

### Gradual Integration

If your horse will be turned out with others, work with the facility to introduce them gradually to the herd.

## Conclusion

With proper preparation and patience, your horse can successfully transition to their new boarding home. Remember that every horse is different—some adapt quickly while others need more time. Trust the process and maintain open communication with your new boarding facility.

Have questions about preparing for a move? Check out our [Help Center](/help) or [contact us](/contact) for personalized guidance.
    `,
    relatedPosts: ['hosting-tips-beginners', 'vaccination-requirements'],
  },
  'hosting-tips-beginners': {
    id: 2,
    title: '10 Tips for New Hosts: Starting Your Boarding Business',
    excerpt: 'Thinking about hosting horses on your property? Learn from experienced hosts about what it takes to run a successful boarding operation.',
    image: 'https://images.unsplash.com/photo-1591330189093-f0a4b8c96a79?w=1200&h=600&fit=crop',
    category: 'Hosting',
    author: 'James Crawford',
    authorImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
    authorBio: 'Former ranch manager passionate about connecting horse owners with quality care.',
    date: '2025-12-28',
    readTime: '10 min read',
    content: `
## Getting Started as a Host

So you have barn space or pasture land and you're thinking about hosting horses? Congratulations on taking the first step toward a rewarding venture! Hosting can provide extra income while allowing you to share your love of horses with others.

Here are 10 essential tips from experienced hosts to help you get started on the right foot.

## Tip 1: Know Your Local Regulations

Before accepting your first boarder, research:

- **Zoning laws** - Ensure your property is zoned for agricultural or equestrian use
- **Business licenses** - Some areas require specific permits for boarding operations
- **Insurance requirements** - Liability coverage is essential
- **Tax implications** - Understand how boarding income affects your taxes

## Tip 2: Assess Your Facilities Honestly

Take a critical look at what you're offering:

- Is your fencing secure and in good repair?
- Do you have adequate shelter for all weather conditions?
- Is there a reliable water source?
- What's the condition of your barn, stalls, or run-in sheds?
- Do you have proper manure management?

Be honest in your listing about what you offer. Setting accurate expectations leads to happier guests.

## Tip 3: Set Clear Policies

Establish policies before your first boarder arrives:

- **Feeding schedules** - What's included vs. extra
- **Turnout policies** - Individual vs. group, hours per day
- **Visitor rules** - When can owners visit? Can they bring guests?
- **Emergency procedures** - Who to call, decision-making authority
- **Payment terms** - Due dates, late fees, deposit requirements

## Tip 4: Price Competitively

Research your local market to set fair prices:

- What do other facilities in your area charge?
- What amenities are included at different price points?
- Consider your costs: feed, bedding, utilities, labor, insurance

Don't underprice yourself—quality care has value. But also be realistic about what your facilities offer compared to full-service operations.

## Tip 5: Invest in Safety

Safety should be your top priority:

- **First aid kit** - Keep a well-stocked equine first aid kit
- **Fire safety** - Fire extinguishers, clear exits, no smoking policies
- **Secure storage** - Lock up feeds and medications
- **Emergency contacts** - Post vet and farrier numbers prominently
- **Documentation** - Keep records of all incidents

## Tip 6: Communication is Key

Good communication prevents most problems:

- Respond promptly to inquiries and messages
- Provide regular updates to owners, especially for full-care boarding
- Be upfront about any issues or concerns
- Document everything in writing

## Tip 7: Build a Network

Connect with local equine professionals:

- Veterinarians who do farm calls
- Farriers with reliable schedules
- Feed suppliers who deliver
- Other hosts for advice and backup

## Tip 8: Start Small

Don't overwhelm yourself:

- Begin with 1-2 boarders to learn the ropes
- Gradually expand as you develop systems
- Know your capacity limits—both physical and time-wise

## Tip 9: Gather Reviews

Positive reviews build your reputation:

- Ask satisfied guests to leave reviews
- Address any concerns promptly and professionally
- Use feedback to improve your services

## Tip 10: Enjoy the Journey

Hosting can be incredibly rewarding:

- You'll meet fellow horse lovers
- Your property stays active and maintained
- You're providing a valuable service to the equestrian community

Remember why you started and take time to enjoy the horses in your care.

## Ready to Get Started?

[Create your listing](/auth?role=host) on Barns & Pastures and join our community of trusted hosts. Our team is here to help you succeed!
    `,
    relatedPosts: ['pricing-your-listing', 'preparing-horse-for-boarding'],
  },
  'cross-country-travel-tips': {
    id: 3,
    title: 'Cross-Country with Horses: Finding Layover Stops',
    excerpt: 'Planning a long-distance move with your horses? We\'ve compiled tips from experienced travelers on finding safe overnight stops.',
    image: 'https://images.unsplash.com/photo-1634212926530-2cf2ac9e0df9?w=1200&h=600&fit=crop',
    category: 'Travel',
    author: 'Emily Rodriguez',
    authorImage: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop',
    authorBio: 'Dedicated to building trust and fostering connections in our host community.',
    date: '2025-12-20',
    readTime: '6 min read',
    content: `
## The Challenge of Long-Distance Horse Travel

Transporting horses across the country is a significant undertaking that requires careful planning. Unlike road trips with dogs where you can stop at almost any hotel, traveling with horses requires finding suitable overnight accommodations along your route.

## Planning Your Route

### Map Your Journey

Before you hit the road:

1. **Plot your starting and ending points**
2. **Calculate total distance and realistic daily travel limits**
3. **Identify potential stopping points every 8-10 hours of driving**
4. **Have backup options in case your first choice is unavailable**

### Consider the Season

- **Summer**: Travel during cooler morning and evening hours
- **Winter**: Watch for ice and snow conditions
- **Spring/Fall**: Be prepared for unpredictable weather

## Finding Quality Layover Stops

### What to Look For

The ideal layover stop should have:

- **Secure fencing** - Safe turnout or at minimum a secure stall
- **Clean water source** - Fresh water available
- **Adequate shelter** - Protection from elements
- **Safe footing** - No hazards in turnout areas
- **Quiet environment** - Away from major highways if possible

### Using Barns & Pastures

Our platform makes finding layover stops easy:

1. Search by your route location
2. Filter for "Layover" listing type
3. Read reviews from other travelers
4. Message hosts with any questions
5. Book with confidence

## Tips for Successful Layovers

### Before You Arrive

- Confirm your reservation 24-48 hours ahead
- Share your ETA with the host
- Ask about any specific requirements

### During the Stop

- Walk horses before putting them in unfamiliar stalls
- Offer water immediately
- Provide hay and allow time to settle
- Check legs and overall condition
- Document any concerns

### Health Considerations

- Carry health certificates and Coggins papers
- Know interstate transport requirements
- Have vet contact information handy
- Watch for signs of shipping fever

## Sample Cross-Country Route

Here's an example of how to plan stops for a trip from California to Florida:

| Day | Start | End | Distance |
|-----|-------|-----|----------|
| 1 | Los Angeles, CA | Phoenix, AZ | ~370 miles |
| 2 | Phoenix, AZ | Las Cruces, NM | ~280 miles |
| 3 | Las Cruces, NM | San Antonio, TX | ~550 miles |
| 4 | San Antonio, TX | Houston, TX | ~200 miles |
| 5 | Houston, TX | New Orleans, LA | ~350 miles |
| 6 | New Orleans, LA | Tallahassee, FL | ~390 miles |
| 7 | Tallahassee, FL | Destination | Varies |

## Emergency Preparedness

Always be prepared for the unexpected:

- **Breakdown kit** - Tools, spare tire, jack
- **Horse emergency kit** - First aid supplies, extra lead ropes
- **Contact list** - Vets along your route, emergency services
- **Cash** - Some rural stops may not accept cards

## Conclusion

With proper planning and the right resources, cross-country travel with horses can be a smooth experience. Start planning your route today and [search for layover stops](/search?type=layover) along the way.

Safe travels! 🐴
    `,
    relatedPosts: ['preparing-horse-for-boarding', 'winter-boarding-considerations'],
  },
  'winter-boarding-considerations': {
    id: 4,
    title: 'Winter Boarding: What to Look For in Cold Weather',
    excerpt: 'Cold weather brings unique challenges for horse boarding. Learn what amenities and preparations matter most during winter months.',
    image: 'https://images.unsplash.com/photo-1551053343-65678990726b?w=1200&h=600&fit=crop',
    category: 'Horse Care',
    author: 'Sarah Mitchell',
    authorImage: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
    authorBio: 'Lifelong equestrian with 20+ years of experience in horse care and stable management.',
    date: '2025-12-15',
    readTime: '7 min read',
    content: `
## Winter Brings Special Challenges

As temperatures drop and days grow shorter, horse care becomes more demanding. Whether you're looking for winter boarding or evaluating your current situation, understanding what matters most in cold weather is essential.

## Key Winter Amenities

### Water Management

The most critical winter concern:

- **Heated waterers** - Prevent freezing and encourage drinking
- **Multiple water sources** - Backup in case one freezes
- **Regular monitoring** - Daily checks to ensure water is accessible

Horses need 5-10 gallons of water daily minimum, and dehydration can lead to colic—a serious risk in winter.

### Shelter Options

Horses need protection from wind, rain, and snow:

- **Run-in sheds** - Allow horses to come and go freely
- **Barn stalls** - Best for severe weather
- **Windbreaks** - Natural or constructed barriers

The key is dry shelter with good ventilation (avoid sealed barns that trap ammonia).

### Footing and Turnout

Winter turnout requires attention to:

- **Mud management** - Gravel pads, drainage systems
- **Ice prevention** - Salt or sand in high-traffic areas
- **Safe footing** - Avoid slick frozen ground

### Feed and Nutrition

Winter increases caloric needs:

- **Increased hay** - Digesting hay generates body heat
- **Quality feed** - Maintained or increased grain rations
- **Storage** - Dry, rodent-free feed storage

## Questions to Ask Potential Winter Boarding Facilities

1. How do you ensure water doesn't freeze?
2. What shelter options are available during severe weather?
3. How do you handle ice and mud in turnout areas?
4. What's your inclement weather policy?
5. How is hay quality maintained through winter?
6. Do you adjust feed based on temperature?
7. What's your blanket policy?
8. How accessible is the facility in snow?

## Preparing Your Horse for Winter Boarding

### Blanketing

Know the facility's policy and provide appropriate blankets:

- **Sheet** - 40-50°F, wind/rain protection
- **Medium weight** - 30-40°F
- **Heavy weight** - Below 30°F

Consider your horse's coat, age, and acclimation.

### Health Checks

Before winter sets in:

- Schedule a vet check
- Update vaccinations
- Ensure dental work is current
- Check body condition—a little extra weight helps in winter

## Red Flags to Avoid

Be cautious of facilities that:

- Have no heated water solution
- Lack adequate shelter
- Don't adjust feeding for temperature
- Have poor drainage/mud management
- Can't be accessed in bad weather

## Conclusion

Winter boarding requires extra attention to detail, but with the right facility, your horse can thrive even in cold weather. Use our [search filters](/search) to find facilities that meet your winter care needs.

Stay warm out there! ❄️
    `,
    relatedPosts: ['preparing-horse-for-boarding', 'vaccination-requirements'],
  },
  'pricing-your-listing': {
    id: 5,
    title: 'How to Price Your Listing: A Host\'s Guide',
    excerpt: 'Setting the right price for your boarding services can be tricky. Here\'s a data-driven approach to competitive pricing.',
    image: 'https://images.unsplash.com/photo-1563116128-261f46267cac?w=1200&h=600&fit=crop',
    category: 'Hosting',
    author: 'James Crawford',
    authorImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
    authorBio: 'Former ranch manager passionate about connecting horse owners with quality care.',
    date: '2025-12-10',
    readTime: '5 min read',
    content: `
## The Art and Science of Pricing

Setting the right price for your boarding services is one of the most important decisions you'll make as a host. Price too high and you'll struggle to attract guests. Price too low and you'll undervalue your services—and potentially your care quality.

## Understanding Your Costs

Before setting prices, calculate your actual costs:

### Fixed Costs (Monthly)
- Property taxes/mortgage
- Insurance
- Utilities
- Equipment maintenance
- Your time value

### Variable Costs (Per Horse)
- Feed and hay
- Bedding
- Manure removal
- Water usage
- Wear and tear

### Example Calculation

| Cost Category | Monthly Amount |
|--------------|----------------|
| Hay (per horse) | $150-300 |
| Grain/supplements | $50-100 |
| Bedding | $30-60 |
| Utilities share | $25-50 |
| Labor value | $100-200 |
| **Base cost** | **$355-710** |

Your price should exceed your base cost to be profitable.

## Market Research

### Study Your Competition

Research other facilities within 30-50 miles:

- What are they charging?
- What's included at each price point?
- What amenities do they offer?
- How do reviews compare?

### Understand Your Market

Consider your area's:

- Average household income
- Density of horse owners
- Availability of boarding options
- Typical price expectations

## Pricing Strategies

### Tiered Pricing

Offer different service levels:

- **Basic/Pasture Board**: $300-400/month
- **Stall Board**: $450-600/month
- **Full Care**: $700-1000+/month

### Seasonal Pricing

Adjust for demand:

- Higher rates during show season
- Discounts for off-peak months
- Premium pricing for holidays

### Length-of-Stay Discounts

Encourage longer commitments:

- **Nightly**: Full rate
- **Weekly**: 10-15% discount
- **Monthly**: 20-25% discount

## Add-On Services

Increase revenue with extras:

- Blanketing: $50-100/month
- Medication administration: $25-50/month
- Extra turnout: $50-100/month
- Training: Market rate
- Grooming: $25-50/session

## Testing Your Prices

### Start Competitive

Begin at or slightly below market rate to:

- Build reviews and reputation
- Learn what guests value
- Understand your actual costs

### Adjust Based on Data

Track:

- Inquiry-to-booking conversion rate
- Length of stays
- Guest feedback on value
- Your actual costs vs. estimates

### When to Raise Prices

Consider increases when:

- You're consistently fully booked
- Costs increase significantly
- You add valuable amenities
- Your reviews justify premium pricing

## Communication is Key

Be transparent about your pricing:

- Clearly list what's included
- Explain any additional fees upfront
- Provide written contracts
- Discuss price changes in advance

## Conclusion

Pricing is both an art and a science. Start with your costs, research your market, and don't be afraid to adjust as you learn. Your goal is fair compensation for quality care.

Ready to set your prices? [Create or update your listing](/host/listings) today!
    `,
    relatedPosts: ['hosting-tips-beginners', 'preparing-horse-for-boarding'],
  },
  'vaccination-requirements': {
    id: 6,
    title: 'Understanding Vaccination Requirements for Boarding',
    excerpt: 'Different facilities have different vaccination requirements. Here\'s what you need to know to keep your horse healthy and compliant.',
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&h=600&fit=crop',
    category: 'Horse Care',
    author: 'Dr. Amanda Foster',
    authorImage: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=100&h=100&fit=crop',
    authorBio: 'Equine veterinarian with 15 years of experience in preventive care and herd health.',
    date: '2025-12-05',
    readTime: '9 min read',
    content: `
## Why Vaccinations Matter for Boarding

When horses from different locations come together at a boarding facility, the risk of disease transmission increases significantly. Vaccinations are the first line of defense in protecting both your horse and others at the facility.

## Core Vaccinations

The American Association of Equine Practitioners (AAEP) recommends these vaccinations for all horses:

### Tetanus

- **What it protects against**: Bacterial infection from wound contamination
- **Frequency**: Initial series, then annual boosters
- **Why it matters**: Tetanus is often fatal; horses are highly susceptible

### Eastern/Western Encephalomyelitis (EEE/WEE)

- **What it protects against**: Mosquito-borne viral brain infections
- **Frequency**: Annual, before mosquito season
- **Why it matters**: High mortality rate, especially for EEE

### West Nile Virus

- **What it protects against**: Mosquito-borne viral infection
- **Frequency**: Annual, before mosquito season
- **Why it matters**: Can cause severe neurological symptoms

### Rabies

- **What it protects against**: Fatal viral disease
- **Frequency**: Annual
- **Why it matters**: Zoonotic (can spread to humans), always fatal

## Risk-Based Vaccinations

Depending on location and exposure risk, facilities may also require:

### Influenza

- **Recommended for**: Horses with frequent contact with other horses
- **Frequency**: Every 6 months for high-risk horses
- **Signs of infection**: Fever, cough, nasal discharge

### Rhinopneumonitis (EHV-1/EHV-4)

- **Recommended for**: All horses in boarding situations
- **Frequency**: Every 6 months
- **Concerns**: Can cause respiratory disease, abortion, and neurological issues

### Strangles (Streptococcus equi)

- **Recommended for**: Horses at high-risk facilities
- **Frequency**: Annual
- **Note**: Some facilities prohibit recent strangles vaccination due to shedding concerns

### Potomac Horse Fever

- **Recommended for**: Horses in endemic areas
- **Frequency**: Annual, before peak season
- **Geographic considerations**: More common in certain regions

## Required Testing

### Coggins Test (EIA)

Most facilities require a current negative Coggins test:

- **What it tests for**: Equine Infectious Anemia
- **Validity**: Typically 12 months
- **Requirement**: Often required by state law for transport

## Timing Considerations

### Before Boarding

- **Ideal timing**: Complete vaccinations 2-4 weeks before arrival
- **Why**: Allows immunity to develop fully
- **Recent vaccinations**: Some facilities have waiting periods after certain vaccines

### Ongoing Requirements

- **Track expiration dates**: Keep a calendar for boosters
- **Facility notifications**: Inform hosts of vaccination schedules
- **Documentation**: Maintain copies of all records

## Working With Your Veterinarian

### Pre-Boarding Exam

Schedule a vet visit that includes:

- Physical examination
- Vaccination updates
- Coggins test if needed
- Health certificate for travel

### Creating a Vaccination Protocol

Discuss with your vet:

- Your horse's specific risk factors
- Regional disease concerns
- Boarding facility requirements
- Optimal timing for boosters

## Common Facility Requirements

| Vaccination | Typical Requirement |
|-------------|---------------------|
| Coggins | Within 12 months |
| Rabies | Within 12 months |
| EEE/WEE | Within 12 months |
| West Nile | Within 12 months |
| Tetanus | Within 12 months |
| Flu/Rhino | Within 6 months |

*Always verify specific requirements with your boarding facility.*

## What If Requirements Differ?

If a facility requires vaccinations your vet doesn't typically recommend:

1. Discuss concerns with your veterinarian
2. Understand the facility's reasoning
3. Make an informed decision
4. Consider alternative facilities if needed

## Conclusion

Vaccination requirements exist to protect all horses at a facility. Stay current on core vaccines, maintain good records, and communicate openly with both your vet and boarding facility.

Questions about health requirements? Check with potential hosts through our [messaging system](/messages) before booking.

Stay healthy! 💉🐴
    `,
    relatedPosts: ['preparing-horse-for-boarding', 'winter-boarding-considerations'],
  },
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const post = blogPostsData[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-farmhouse-charcoal flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-farmhouse-cream mb-4">Article Not Found</h1>
          <p className="text-farmhouse-muted mb-6">The article you're looking for doesn't exist.</p>
          <Link to="/blog">
            <Button className="btn-primary">Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = window.location.href;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const relatedPosts = post.relatedPosts
    ?.map(slug => blogPostsData[slug])
    .filter(Boolean) || [];

  return (
    <div className="min-h-screen bg-farmhouse-charcoal" data-testid="blog-post-page">
      {/* Hero Image */}
      <div className="relative h-[50vh] md:h-[60vh]">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-farmhouse-charcoal via-farmhouse-charcoal/50 to-transparent" />
        
        {/* Back button */}
        <button
          onClick={() => navigate('/blog')}
          className="absolute top-4 left-4 flex items-center gap-2 px-4 py-2 bg-farmhouse-charcoal/80 rounded-md text-white hover:bg-farmhouse-charcoal transition-colors backdrop-blur-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </button>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 -mt-32 relative z-10">
        <article className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-8 md:p-12">
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <Badge className="bg-farmhouse-saddle/20 text-farmhouse-saddle border-0">
              {post.category}
            </Badge>
            <span className="text-farmhouse-muted text-sm flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
            <span className="text-farmhouse-muted text-sm flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {post.readTime}
            </span>
          </div>

          {/* Title */}
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-farmhouse-cream mb-6 leading-tight">
            {post.title}
          </h1>

          {/* Author */}
          <div className="flex items-center gap-4 pb-8 border-b border-farmhouse-subtle mb-8">
            <img
              src={post.authorImage}
              alt={post.author}
              className="w-14 h-14 rounded-full object-cover border-2 border-farmhouse-saddle"
            />
            <div>
              <p className="font-medium text-farmhouse-cream">{post.author}</p>
              <p className="text-farmhouse-muted text-sm">{post.authorBio}</p>
            </div>
          </div>

          {/* Content */}
          <div 
            className="prose prose-invert prose-farmhouse max-w-none
              prose-headings:font-serif prose-headings:text-farmhouse-cream
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-farmhouse-muted prose-p:leading-relaxed prose-p:mb-4
              prose-a:text-farmhouse-saddle prose-a:no-underline hover:prose-a:underline
              prose-strong:text-farmhouse-cream
              prose-ul:text-farmhouse-muted prose-ul:my-4
              prose-ol:text-farmhouse-muted prose-ol:my-4
              prose-li:my-1
              prose-table:my-6
              prose-th:text-farmhouse-cream prose-th:bg-farmhouse-charcoal prose-th:p-3
              prose-td:text-farmhouse-muted prose-td:p-3 prose-td:border-farmhouse-subtle
            "
            dangerouslySetInnerHTML={{ 
              __html: post.content
                .replace(/^## /gm, '<h2>')
                .replace(/^### /gm, '<h3>')
                .replace(/\n\n/g, '</p><p>')
                .replace(/<h2>/g, '</p><h2>')
                .replace(/<h3>/g, '</p><h3>')
                .replace(/<\/h2>\s*<\/p>/g, '</h2>')
                .replace(/<\/h3>\s*<\/p>/g, '</h3>')
                .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
                .replace(/^- /gm, '<li>')
                .replace(/<li>/g, '</li><li>')
                .replace(/\| ([^|]+) \|/g, '<td>$1</td>')
            }}
          />

          {/* Share */}
          <div className="mt-12 pt-8 border-t border-farmhouse-subtle">
            <p className="text-farmhouse-muted mb-4">Share this article</p>
            <div className="flex items-center gap-3">
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-farmhouse-charcoal rounded-md text-farmhouse-muted hover:text-[#1877F2] transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-farmhouse-charcoal rounded-md text-farmhouse-muted hover:text-[#1DA1F2] transition-colors"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(post.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-farmhouse-charcoal rounded-md text-farmhouse-muted hover:text-[#0A66C2] transition-colors"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <button
                onClick={handleCopyLink}
                className="p-3 bg-farmhouse-charcoal rounded-md text-farmhouse-muted hover:text-farmhouse-saddle transition-colors"
              >
                {copied ? <CheckCircle className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="mt-12 mb-16">
            <h2 className="font-serif text-2xl font-semibold text-farmhouse-cream mb-6">
              Related Articles
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {relatedPosts.map((relPost) => (
                <Link
                  key={relPost.id}
                  to={`/blog/${Object.keys(blogPostsData).find(key => blogPostsData[key].id === relPost.id)}`}
                  className="group bg-farmhouse-paper border border-farmhouse-subtle rounded-md overflow-hidden card-hover"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={relPost.image}
                      alt={relPost.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <Badge className="bg-farmhouse-saddle/20 text-farmhouse-saddle border-0 mb-2">
                      {relPost.category}
                    </Badge>
                    <h3 className="font-serif text-lg font-semibold text-farmhouse-cream group-hover:text-farmhouse-saddle transition-colors">
                      {relPost.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
